import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getCurrentUser, logoutUser } from "../../services/auth";
import { getUserProfile } from "../../services/api";
import { addAuthEventListeners, dispatchLogoutEvent } from "../../utils/auth-events";

const Header = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(getCurrentUser());
  const [userProfile, setUserProfile] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // Fetch user profile when user is available
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user) {
        try {
          const profile = await getUserProfile();
          setUserProfile(profile);
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      } else {
        setUserProfile(null);
      }
    };

    fetchUserProfile();
  }, [user]);

  // Update user state when token changes
  useEffect(() => {
    const handleAuthChange = () => {
      setUser(getCurrentUser());
    };

    // Use the utility function to add auth event listeners
    const cleanup = addAuthEventListeners(handleAuthChange);

    return cleanup;
  }, []);

  // Handle scroll events to detect when to shrink navbar
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    // Show confirmation dialog
    if (window.confirm('Are you sure you want to logout?')) {
      logoutUser();
      
      // Dispatch custom event to update Header
      dispatchLogoutEvent();
      
      // Update local state immediately
      setUser(null);
      setUserProfile(null);
      
      // Redirect to home page
      navigate("/");
      // Force page reload to clear all state
      window.location.reload();
    }
  };

  return (
    <>
    <nav 
      className={`navbar navbar-expand-lg navbar-conference ${isScrolled ? 'scrolled' : ''}`}
      style={{
        backgroundImage: 'url(/images/background.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1030,
        minHeight: isScrolled ? '90px' : '140px',
        transition: 'all 0.3s ease',
        paddingTop: isScrolled ? '1rem' : '1.5rem',
        paddingBottom: isScrolled ? '1rem' : '1.5rem'
      }}
    >
      {/* Overlay for better text readability */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          // backgroundColor: isScrolled ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.2)',
          zIndex: 1,
          transition: 'all 0.3s ease'
        }}
      ></div>
      
      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        {/* Brand with Logo */}
        <Link className="navbar-brand fw-bold d-flex align-items-center" to="/">
          <img 
            src="/images/logo_mfs.png" 
            alt="Multinational Finance Society" 
            style={{
              height: isScrolled ? '50px' : '80px',
              width: 'auto',
              objectFit: 'contain',
              filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.5))',
              transition: 'all 0.3s ease'
            }}
          />
        </Link>

        {/* Mobile Toggle Button */}
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
          style={{
            borderColor: 'rgba(0, 0, 0, 0.5)'
          }}
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        {/* Navigation Links */}
        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav me-auto">
            <li className="nav-item">
              <Link 
                className="nav-link" 
                to="/"
              >
                <i className="bi bi-house me-1"></i>
                Home
              </Link>
            </li>
            <li className="nav-item dropdown">
              <a
                className="nav-link dropdown-toggle"
                href="#"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="bi bi-building me-1"></i>
                The Society
              </a>
              <ul className="dropdown-menu" style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
                <li>
                  <Link className="dropdown-item" to="/society">
                    <i className="bi bi-info-circle me-2"></i>
                    About the Society
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/conferences">
                    <i className="bi bi-calendar-event me-2"></i>
                    Conferences
                  </Link>
                </li>
                <li>
                  <hr className="dropdown-divider" />
                </li>
                <li>
                  <Link className="dropdown-item" to="/society/board">
                    <i className="bi bi-people me-2"></i>
                    Board of Directors
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/membership">
                    <i className="bi bi-person-badge me-2"></i>
                    Membership
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/journal">
                    <i className="bi bi-journal-text me-2"></i>
                    Multinational Finance Journal
                  </Link>
                </li>

              </ul>
            </li>
            {/* <li className="nav-item">
              <Link 
                className="nav-link" 
                to="/program"
              >
                <i className="bi bi-calendar-event me-1"></i>
                Program
              </Link>
            </li>
            <li className="nav-item">
              <Link 
                className="nav-link" 
                to="/dashboard"
              >
                <i className="bi bi-file-earmark-text me-1"></i>
                Call for Papers
              </Link>
            </li> */}
          </ul>

          {/* Right side - Auth buttons */}
          <ul className="navbar-nav">
            {!user ? (
              // Show when user is NOT signed in
              <>
                <li className="nav-item">
                  <Link 
                    className="nav-link" 
                    to="/login"
                  >
                    <i className="bi bi-box-arrow-in-right me-1"></i>
                    Login
                  </Link>
                </li>
                {/* <li className="nav-item">
                  <Link
                    className="nav-link"
                    to="/register"
                  >
                    <i className="bi bi-person-plus me-1"></i>
                    Register
                  </Link>
                </li> */}
              </>
            ) : (
              // Show when user IS signed in
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle d-flex align-items-center"
                  href="#"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="bi bi-person-circle me-2"></i>
                  {userProfile?.firstName ? `${userProfile.firstName} ${userProfile.lastName}` : user.username || user.email}
                </a>
                <ul className="dropdown-menu dropdown-menu-end" style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>

                  {user && user.role === "admin" && (
                    <li>
                      <Link className="dropdown-item" to="/admin/dashboard">
                        <i className="bi bi-file-earmark-bar-graph me-2"></i>
                        Admin
                      </Link>
                    </li>
                  )}
                  <li>
                    <Link className="dropdown-item" to="/dashboard">
                      <i className="bi bi-speedometer2 me-2"></i>
                      Dashboard
                    </Link>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>                  <li>
                    <Link className="dropdown-item" to="/profile">
                      <i className="bi bi-person me-2"></i>
                      Profile
                    </Link>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <button
                      className="dropdown-item text-danger"
                      onClick={handleLogout}
                      style={{ border: 'none', background: 'none', width: '100%', textAlign: 'left' }}
                    >
                      <i className="bi bi-box-arrow-right me-2"></i>
                      Logout
                    </button>
                  </li>
                </ul>
              </li>
            )}
          </ul>
        </div>
      </div>
    </nav>
    <div                   
      style={{ 
        height: 140, 
      }}>
    </div>
    </>
  );
};

export default Header;
